import { useForm } from 'react-hook-form';
import { fetcher } from '@bino/utils/fetcher';
import { BiButton, BiButtonGroup, BiInputField, BiLink, BiSelect, BiSelectOption } from 'bi-web-components-react';
import { useTranslation } from 'react-i18next';
import { ChatFeedback } from '../chatFeedback/chatFeedback';
import styles from './contactForm.module.css';
import useSWRImmutable from 'swr/immutable';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { ComponentSelector } from '../../types';

export type ContactFormProps = {
    setComponent: (val: ComponentSelector) => void;
};

const contactFormSchema = z.object({
    firstname: z.string({ error: 'firstnameValidator' }),
    lastname: z.string({ error: 'lastnameValidator' }),
    email: z.string({ error: (issue) => (issue.input === undefined ? 'mailValidator' : 'mailFormatValidator') }),
    category: z.string().min(1, 'catValidator'),
    message: z.string().min(3, 'msgValidator'),
});

const contactFormDataStep = 1;
const confirmStep = 2;
const errorStep = 3;
type ContactFormValues = z.infer<typeof contactFormSchema>;

export const ContactForm = ({ setComponent }: ContactFormProps) => {
    const { t, i18n } = useTranslation('chat');
    const { data: categories = [] } = useSWRImmutable(
        '/api/sf-case/categories',
        fetcher.get<Array<{ value: string; displayValue: { no: string; en: string } }>>,
        { keepPreviousData: true },
    );

    const [wizardStep, setWizardStep] = useState(contactFormDataStep);
    const [isSaving, setIsSaving] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
    });

    const navigateToFront = () => {
        setComponent('front');
    };

    const onSubmit = async (data: ContactFormValues) => {
        setIsSaving(true);

        try {
            await fetcher.post<{ message: string }>('/api/sf-case/contact-us', {
                FirstName: data.firstname,
                LastName: data.lastname,
                Email: data.email,
                Category: data.category,
                Message: data.message,
                PreferredLanguage: i18n.language,
            });
            setIsSaving(false);
            setWizardStep(confirmStep);
        } catch {
            setIsSaving(false);
            setWizardStep(errorStep);
        }
    };

    if (wizardStep === contactFormDataStep) {
        return (
            <div className={styles.contactForm}>
                <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                    <BiInputField
                        required
                        label={t('firstname')}
                        {...register('firstname')}
                        errorMessage={t(errors.firstname?.message || '')}
                        error={!!errors.firstname}
                    />

                    <BiInputField
                        required
                        label={t('lastname')}
                        {...register('lastname')}
                        errorMessage={t(errors.lastname?.message || '')}
                        error={!!errors.lastname}
                    />

                    <BiInputField
                        required
                        label={t('email')}
                        {...register('email')}
                        errorMessage={t(errors.email?.message || '')}
                        error={!!errors.email}
                    />

                    <BiSelect
                        errorMessage={t(errors.category?.message || '')}
                        error={!!errors.category}
                        required={true}
                        label={t('category')}
                        {...register('category')}
                    >
                        {categories.map((category, idx) => (
                            <BiSelectOption
                                key={`${category.value}-${idx}`}
                                label={category.displayValue[i18n.language as 'no' | 'en']}
                                value={category.value}
                            />
                        ))}
                    </BiSelect>

                    <BiInputField
                        required
                        label={t('descPlaceholder')}
                        placeholder={t('descPlaceholder')}
                        {...register('message')}
                        errorMessage={t(errors.message?.message || '')}
                        error={!!errors.message}
                        rows={5}
                        size='long'
                    />

                    <p className={styles.privacyStatement}>
                        {t('gdpr')}
                        <BiLink
                            color='blue'
                            href={
                                i18n.language === 'no'
                                    ? 'https://www.bi.no/om-bi/personvern-og-cookies/'
                                    : 'https://www.bi.no/en/about-bi/privacy-policy-and-cookies/'
                            }
                            size='small'
                            state='noline'
                            target='_blank'
                        >
                            {t('gdprPrivacy')}
                        </BiLink>
                    </p>

                    <BiButtonGroup spacing='medium' alignment='center'>
                        <BiButton variant='outlined' onClick={navigateToFront}>
                            {t('back')}
                        </BiButton>
                        <BiButton type='submit' submitting={isSaving} disabled={isSaving} variant='filled'>
                            {t('submitButton')}
                        </BiButton>
                    </BiButtonGroup>
                </form>
            </div>
        );
    }

    return (
        <ChatFeedback type={wizardStep === confirmStep ? 'confirmation' : 'error'} onNavigateBack={navigateToFront} />
    );
};
